import { Injectable, NgZone } from '@angular/core';
import { Title } from '@angular/platform-browser';
import { ActivatedRouteSnapshot, Router, UrlTree } from '@angular/router';
import { Action, Selector, State, StateContext } from '@ngxs/store';
import { isEqual } from 'lodash-es';
import moment from 'moment';
import { map, switchMap } from 'rxjs/operators';
import { Observable } from 'rxjs';
import { Prompt } from '@nimbus-library/features/authentication/models/authentication.enum';
import { IAuthenticationService } from '@nimbus-library/features/authentication/services/i-authentication.service';
import { CacheService, IEntityCacheService } from '@nimbus-library/features/cache/index';
import { MenuEntryViewModel } from '@nimbus-library/features/menu/models/menu-entry.view-model';
import { MenuViewModel } from '@nimbus-library/features/menu/models/menu.view-model';
import { IMenuProviderService } from '@nimbus-library/features/menu/services/contracts/i-menu-provider.service';
import { ITitleProviderService } from '@nimbus-library/features/menu/services/contracts/i-title-provider.service';
import { NavigationActions } from '@nimbus-library/features/navigation/navigation.actions';
import { MenuActions } from './menu.actions';

export interface MenuStateModel {
  error?: unknown;
  show: boolean;
  menu?: MenuViewModel;
  storedUrl?: string;
  activatedRoute?: ActivatedRouteSnapshot;
  urlAfterRedirects?: UrlTree;
  title?: string | ((...parameters: any[]) => string);
  previousEntry?: MenuEntryViewModel;
  activeEntry?: MenuEntryViewModel;
}

@State<MenuStateModel>({
  name: 'menuState',
  defaults: {
    show: true,
  },
})
@Injectable()
export class MenuState {
  private readonly menuItemCache: IEntityCacheService<string>;

  public constructor(
    private readonly router: Router,
    private readonly ngZone: NgZone,
    private readonly titleService: Title,
    private readonly titleProviderService: ITitleProviderService,
    private readonly menuProviderService: IMenuProviderService,
    private readonly authenticationService: IAuthenticationService,
    cacheService: CacheService,
  ) {
    this.menuItemCache = cacheService.for<string>('menuItemCache', { preloadMaxAge: moment.duration(1, 'week') });
  }

  @Selector()
  public static error(state: MenuStateModel): unknown {
    return state.error;
  }

  @Selector()
  public static menu(state: MenuStateModel): MenuViewModel {
    return state.menu;
  }

  @Selector()
  public static menuEntries(state: MenuStateModel): MenuEntryViewModel {
    return state.menu.brand;
  }

  @Selector()
  public static activationHistory(state: MenuStateModel): { current: MenuEntryViewModel; prev: MenuEntryViewModel } {
    return { current: state.activeEntry, prev: state.previousEntry };
  }

  @Selector()
  public static show(state: MenuStateModel): boolean {
    return state.show && !!state.menu;
  }

  @Selector()
  public static activeMenuEntry(state: MenuStateModel): MenuEntryViewModel {
    return state.activeEntry;
  }

  @Selector()
  public static route(state: MenuStateModel): ActivatedRouteSnapshot {
    return state.activatedRoute;
  }

  @Selector()
  public static urlAfterRedirect(state: MenuStateModel): UrlTree {
    return state.urlAfterRedirects;
  }

  @Action(NavigationActions.NavigationEnd, { cancelUncompleted: true })
  public updateMenuStructure(
    { dispatch, patchState }: StateContext<MenuStateModel>,
    { activatedRoute, urlAfterRedirects }: NavigationActions.NavigationEnd,
  ): Observable<void> {
    const title$ = this.titleProviderService.navigationEvent(activatedRoute, urlAfterRedirects);
    patchState({
      urlAfterRedirects: urlAfterRedirects ? this.router.parseUrl(urlAfterRedirects) : null,
      activatedRoute,
    });

    return title$.pipe(switchMap((title) => dispatch(new MenuActions.SetTitle(title))));
  }

  @Action(MenuActions.SetTitle)
  public setTitle({ getState, patchState }: StateContext<MenuStateModel>, { title }: MenuActions.SetTitle): void {
    const currentState = getState();

    if (!isEqual(currentState.title, title)) {
      patchState({
        title,
      });

      if (title && typeof title !== 'function') {
        this.titleService.setTitle(title);
      }
    }
  }

  @Action(MenuActions.TitleParametersLoaded)
  public titleParametersLoaded({ getState }: StateContext<MenuStateModel>, { parameters }: MenuActions.TitleParametersLoaded): void {
    const title = getState().title;

    if (title && typeof title === 'function') {
      this.titleService.setTitle(title(...parameters));
    }
  }

  @Action(MenuActions.StoreUrl)
  public storeUrl({}: StateContext<MenuStateModel>, { parent, url }: MenuActions.StoreUrl): void {
    this.menuItemCache.set<string>(parent, url);
  }

  @Action(MenuActions.GetStoredUrl)
  public getStoredUrl({ patchState }: StateContext<MenuStateModel>, { parent }: MenuActions.GetStoredUrl): void {
    patchState({
      storedUrl: this.menuItemCache.get<string>(parent),
    });
  }

  @Action(MenuActions.Navigate)
  public navigate(
    { dispatch, getState }: StateContext<MenuStateModel>,
    { entry }: MenuActions.Navigate,
  ): Observable<void> | Promise<boolean> {
    if (entry.subEntries && entry.subEntries.length > 0) {
      return dispatch(new MenuActions.GetStoredUrl(entry.name)).pipe(
        map(() => {
          const urlToNavigate = getState().storedUrl;
          return this.ngZone.run(() => {
            this.router.navigate([urlToNavigate || entry.subEntries[0].path]);
          });
        }),
      );
    } else {
      return this.ngZone.run(() => this.router.navigate([entry.path]));
    }
  }

  @Action(MenuActions.SetActiveMenuEntry)
  public setActiveMenuEntry(
    { patchState, getState }: StateContext<MenuStateModel>,
    { menuActiveEntry }: MenuActions.SetActiveMenuEntry,
  ): void {
    if (!isEqual(getState().activeEntry, menuActiveEntry)) {
      patchState({
        previousEntry: getState().activeEntry,
        activeEntry: menuActiveEntry,
      });
    }
  }

  @Action(MenuActions.Initialize)
  public initialize({ dispatch }: StateContext<MenuStateModel>): Observable<void> {
    return this.menuProviderService.getMenu().pipe(switchMap((menu) => dispatch(new MenuActions.SetMenu(menu))));
  }

  @Action(MenuActions.SetMenu)
  public setMenu({ patchState, getState }: StateContext<MenuStateModel>, { menu }: MenuActions.SetMenu): void {
    if (!menu) {
      patchState({ show: false });
    } else {
      if (!isEqual(menu, getState().menu)) {
        patchState({
          show: true,
          menu,
        });
      }
    }
  }

  @Action(MenuActions.LogOut)
  public logout({}: StateContext<MenuStateModel>): void {
    this.authenticationService.logout(
      true,
      {
        autoLogin: false,
        returnUrl: this.authenticationService.getReturnUrl(),
        prompt: Prompt.SelectAccount,
      },
      true,
    );
  }
}
